import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Users, ArrowLeft, LogIn, UserPlus, Loader2, Building2, Sparkles } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';

export default function IndustryAuth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isIndustry, loginIndustry, API } = useAuth();
  
  const mode = searchParams.get('mode') || 'login';
  const nextAction = searchParams.get('next');
  const [pendingPrompt, setPendingPrompt] = useState('');
  const [activeTab, setActiveTab] = useState(mode === 'signup' ? 'signup' : 'login');
  const [loading, setLoading] = useState(false);
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [signupData, setSignupData] = useState({ 
    email: '', 
    password: '', 
    confirmPassword: '',
    company_name: '' 
  });

  useEffect(() => {
    // Read the stashed prompt (if any) so we can show a preview + auto-create after auth
    try {
      const p = sessionStorage.getItem('pending_project_prompt');
      if (p) {
        setPendingPrompt(p);
        setActiveTab('signup'); // default to signup for a smoother first-time flow
      }
    } catch (e) { /* noop */ }
  }, []);

  useEffect(() => {
    if (isAuthenticated && isIndustry) {
      // If there's a pending prompt from the landing page, create the project now
      completePendingProject();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isIndustry]);

  const completePendingProject = async () => {
    try {
      const p = sessionStorage.getItem('pending_project_prompt');
      if (!p) {
        navigate('/industry/projects');
        return;
      }
      const t = localStorage.getItem('collabhive_token');
      if (!t) return;
      const res = await fetch(`${API}/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` },
        body: JSON.stringify({ prompt: p })
      });
      sessionStorage.removeItem('pending_project_prompt');
      if (res.ok) {
        const data = await res.json();
        toast.success('Drafting your proposal…');
        navigate(`/industry/projects/${data.id}`);
      } else {
        navigate('/industry/projects');
      }
    } catch (e) {
      navigate('/industry/projects');
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!loginData.email || !loginData.password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/industry/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });
      
      if (response.ok) {
        const data = await response.json();
        loginIndustry(data.access_token, data.user_id);
        toast.success('Welcome back!');
        // The useEffect above will pick up the pending prompt and auto-create the project
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Login failed');
      }
    } catch (error) {
      toast.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const refToken = searchParams.get('ref');
  const [refUniName, setRefUniName] = useState('');

  useEffect(() => {
    if (refToken) {
      // Look up university name for a friendly banner
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/universities/by-token/${refToken}`)
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d && d.kind === 'industry') setRefUniName(d.name); });
    }
  }, [refToken]);

  const handleSignup = async (e) => {
    e.preventDefault();
    
    if (!signupData.email || !signupData.password || !signupData.confirmPassword) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (signupData.password !== signupData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (signupData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/industry/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: signupData.email,
          password: signupData.password,
          company_name: signupData.company_name || null,
          ref_token: refToken || null,
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        loginIndustry(data.access_token, data.user_id);
        toast.success('Account created successfully!');
        // The useEffect above will pick up the pending prompt and auto-create the project
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Signup failed');
      }
    } catch (error) {
      toast.error('Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Navigation */}
      <nav className="nav-header">
        <div className="container-main py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-md gradient-primary flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div className="flex flex-col leading-none">
              <span className="text-xl font-semibold tracking-tight">CollabHive</span>
              <span className="text-[10px] text-muted-foreground mt-0.5">Powered by PM</span>
            </div>
          </Link>
          
          <Link to="/">
            <Button variant="ghost" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </nav>

      <div className="container-main py-12">
        <div className="max-w-3xl mx-auto space-y-6">
          {pendingPrompt && (
            <Card className="animate-fade-in border-primary/40 bg-primary/5">
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  <Sparkles className="w-5 h-5 text-primary mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold text-sm mb-1">Your proposal is one step away</p>
                    <p className="text-xs text-muted-foreground mb-3">
                      Sign in or create a free account below — we&apos;ll instantly generate a full
                      Project Proposal from your requirement.
                    </p>
                    <div className="relative rounded-md border bg-background/60 p-3">
                      <p className="text-sm text-muted-foreground italic line-clamp-2 select-none blur-[2px]">
                        {pendingPrompt}
                      </p>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-[10px] uppercase tracking-wider font-semibold text-primary bg-background/80 px-2 py-0.5 rounded">
                          Locked — sign in to view
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        <div className="max-w-md mx-auto">
          <Card className="animate-fade-in">
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-primary" />
              </div>
              <CardTitle className="text-2xl">{pendingPrompt ? 'Unlock Your Proposal' : 'Industry Access'}</CardTitle>
              <CardDescription>
                {pendingPrompt ? 'Sign in or create a free account to generate your proposal' : 'Sign in or create an account to start your project'}
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="login" className="gap-2">
                    <LogIn className="w-4 h-4" />
                    Login
                  </TabsTrigger>
                  <TabsTrigger value="signup" className="gap-2">
                    <UserPlus className="w-4 h-4" />
                    Sign Up
                  </TabsTrigger>
                </TabsList>
                
                {/* Login Form */}
                <TabsContent value="login">
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="your@email.com"
                        value={loginData.email}
                        onChange={(e) => setLoginData(prev => ({ ...prev, email: e.target.value }))}
                        data-testid="login-email"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Password</Label>
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        value={loginData.password}
                        onChange={(e) => setLoginData(prev => ({ ...prev, password: e.target.value }))}
                        data-testid="login-password"
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full gap-2" 
                      disabled={loading}
                      data-testid="login-submit"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Logging in...
                        </>
                      ) : (
                        <>
                          <LogIn className="w-4 h-4" />
                          Login
                        </>
                      )}
                    </Button>
                  </form>
                </TabsContent>
                
                {/* Signup Form */}
                <TabsContent value="signup">
                  {refUniName && (
                    <div className="mb-4 p-3 rounded-md bg-primary/5 border border-primary/20 text-sm" data-testid="industry-ref-banner">
                      You were invited by <b>{refUniName}</b>. Your account will be linked to this institution.
                    </div>
                  )}
                  <form onSubmit={handleSignup} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="signup-email">Email *</Label>
                      <Input
                        id="signup-email"
                        type="email"
                        placeholder="your@email.com"
                        value={signupData.email}
                        onChange={(e) => setSignupData(prev => ({ ...prev, email: e.target.value }))}
                        data-testid="signup-email"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="signup-company">Company Name (Optional)</Label>
                      <Input
                        id="signup-company"
                        type="text"
                        placeholder="Your Company Inc."
                        value={signupData.company_name}
                        onChange={(e) => setSignupData(prev => ({ ...prev, company_name: e.target.value }))}
                        data-testid="signup-company"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="signup-password">Password *</Label>
                      <Input
                        id="signup-password"
                        type="password"
                        placeholder="Min. 6 characters"
                        value={signupData.password}
                        onChange={(e) => setSignupData(prev => ({ ...prev, password: e.target.value }))}
                        data-testid="signup-password"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="signup-confirm">Confirm Password *</Label>
                      <Input
                        id="signup-confirm"
                        type="password"
                        placeholder="Confirm your password"
                        value={signupData.confirmPassword}
                        onChange={(e) => setSignupData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        data-testid="signup-confirm"
                      />
                    </div>
                    
                    <Button 
                      type="submit" 
                      className="w-full gap-2" 
                      disabled={loading}
                      data-testid="signup-submit"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Creating account...
                        </>
                      ) : (
                        <>
                          <UserPlus className="w-4 h-4" />
                          Create Account
                        </>
                      )}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>

              <div className="mt-6 pt-6 border-t text-center">
                <p className="text-sm text-muted-foreground">
                  Are you an expert?{' '}
                  <Link to="/expert/signup" className="text-primary hover:underline font-medium">
                    Sign up with LinkedIn
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
        </div>
      </div>
    </div>
  );
}
